import { useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { API_BASE_URL } from "../../api/config/apiConfig";
import { notificationApi } from "../../api/notificationApi";

const QUICK_PAGE_SIZE = 6;
const LIST_PAGE_SIZE = 10;
const unwrap = (payload) => payload?.data ?? payload?.Data ?? payload ?? {};

const typeClass = (type) => {
  if (type === "review.completed") return "type-success";
  if (type === "warning") return "type-warning";
  if (type === "error") return "type-error";
  return "type-info";
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [listItems, setListItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [pageSize] = useState(LIST_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [loadingQuick, setLoadingQuick] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const wrapRef = useRef(null);
  const seeAllRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadQuick = async () => {
    setLoadingQuick(true);
    try {
      const result = unwrap(await notificationApi.getMine(1, QUICK_PAGE_SIZE));
      setItems(result?.items || []);
      setUnreadCount(result?.unreadCount || 0);
    } finally {
      setLoadingQuick(false);
    }
  };

  const loadPage = async (page = 1) => {
    setLoadingList(true);
    try {
      const result = unwrap(await notificationApi.getMine(page, pageSize));
      setListItems(result?.items || []);
      setTotalCount(result?.totalCount || 0);
      setUnreadCount(result?.unreadCount || 0);
      setListPage(result?.pageNumber || page);
    } finally {
      setLoadingList(false);
    }
  };

  const refreshAll = async () => {
    await loadQuick();
    if (seeAllRef.current) {
      await loadPage(listPage);
    }
  };

  useEffect(() => {
    loadQuick();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    const hubUrl = new URL("/hubs/notifications", API_BASE_URL).toString();
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem("accessToken") || "",
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("notification.received", (rawMessage) => {
      refreshAll();
      if (rawMessage?.type === "review.completed") {
        setOpen(true);
      }
    });

    connection.onreconnecting(() => setConnected(false));
    connection.onreconnected(() => setConnected(true));
    connection.onclose(() => setConnected(false));

    connection
      .start()
      .then(() => setConnected(true))
      .catch(() => setConnected(false));

    return () => {
      connection.off("notification.received");
      connection.stop();
    };
  }, []);

  const unread = useMemo(() => {
    return unreadCount;
  }, [unreadCount]);

  const bellIcon = useMemo(() => {
    if (unread > 0) return "bi-bell-fill";
    return "bi-bell";
  }, [unread]);

  const markAsRead = async (id) => {
    await notificationApi.markRead(id);
    await refreshAll();
    if (seeAllRef.current) {
      await loadPage(listPage);
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const result = unwrap(await notificationApi.getById(id));
      setDetail(result);
      if (!result?.isRead) {
        await markAsRead(id);
        setDetail((prev) => (prev ? { ...prev, isRead: true, readAt: new Date().toISOString() } : prev));
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const openSeeAll = async () => {
    setSeeAllOpen(true);
    seeAllRef.current = true;
    await loadPage(1);
  };

  const closeSeeAll = () => {
    setSeeAllOpen(false);
    seeAllRef.current = false;
    setDetail(null);
  };

  const markAllRead = async () => {
    await notificationApi.markAllRead();
    await refreshAll();
    if (seeAllRef.current) {
      await loadPage(listPage);
      if (detail?.id) {
        await openDetail(detail.id);
      }
    }
  };

  useEffect(() => {
    if (open) {
      loadQuick();
    }
  }, [open]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="notification-wrap" ref={wrapRef}>
      <button
        type="button"
        className="btn notification-trigger"
        onClick={() => setOpen((prev) => !prev)}
        title="Notifications"
        aria-label="Notifications"
      >
        <i className={`bi ${bellIcon}`}></i>
        {unread > 0 && <span className="notification-badge">{unread > 99 ? "99+" : unread}</span>}
        <span className={`notification-dot ${connected ? "online" : "offline"}`}></span>
      </button>

      {open && (
        <div className="notification-panel shadow">
          <div className="notification-panel-head">
            <strong>Notifications</strong>
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-sm btn-link" onClick={markAllRead}>Mark all read</button>
              <button type="button" className="btn btn-sm btn-link" onClick={openSeeAll}>See all</button>
            </div>
          </div>

          <div className="notification-panel-body">
            {loadingQuick && (
              <div className="notification-empty">Loading...</div>
            )}

            {!loadingQuick && items.length === 0 && (
              <div className="notification-empty">Chưa có thông báo mới.</div>
            )}

            {items.map((item) => (
              <div
                key={item.id}
                className={`notification-item ${typeClass(item.type)} ${item.isRead ? "read" : "unread"}`}
                onClick={() => {
                  setOpen(false);
                  openSeeAll().then(() => openDetail(item.id));
                }}
              >
                <div className="notification-item-title">{item.title}</div>
                <div className="notification-item-message">{item.message}</div>
                <div className="notification-item-time">
                  {new Date(item.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {seeAllOpen && (
        <div className="notification-overlay" onClick={closeSeeAll}>
          <div className="notification-modal shadow" onClick={(event) => event.stopPropagation()}>
            <div className="notification-modal-head">
              <strong>All Notifications</strong>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeSeeAll}>
                Close
              </button>
            </div>

            <div className="notification-modal-body">
              <div>
                {loadingList && <div className="notification-empty">Loading...</div>}
                {!loadingList && listItems.length === 0 && <div className="notification-empty">No notifications.</div>}

                {listItems.map((item) => (
                  <div
                    key={item.id}
                    className={`notification-item ${typeClass(item.type)} ${item.isRead ? "read" : "unread"}`}
                    onClick={() => openDetail(item.id)}
                  >
                    <div className="notification-item-title">{item.title}</div>
                    <div className="notification-item-message">{item.message}</div>
                    <div className="notification-item-time">
                      {new Date(item.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))}

                <div className="notification-pagination">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={listPage <= 1}
                    onClick={() => loadPage(listPage - 1)}
                  >
                    Prev
                  </button>
                  <span>Page {listPage}/{totalPages}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={listPage >= totalPages}
                    onClick={() => loadPage(listPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="notification-detail-panel">
                {detailLoading && <div className="notification-empty">Loading detail...</div>}
                {!detailLoading && !detail && <div className="notification-empty">Chọn thông báo để xem chi tiết.</div>}
                {!detailLoading && detail && (
                  <>
                    <h6>{detail.title}</h6>
                    <div className="notification-item-time">
                      {new Date(detail.createdAt).toLocaleString()}
                    </div>
                    <p className="mb-2">{detail.message}</p>
                    <div className="notification-item-time">
                      Type: {detail.type} | Read: {detail.isRead ? "Yes" : "No"}
                    </div>
                    {detail.payloadJson && (
                      <pre className="notification-payload">{detail.payloadJson}</pre>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
